import React, { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref as dbRef, onValue, update } from 'firebase/database';
import {
  FaBox,
  FaShoppingBag,
  FaCalendar,
  FaCreditCard,
  FaMapMarkerAlt,
  FaPhone,
  FaTruck,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaClock,
  FaTimesCircle,
  FaImage,
  FaMoneyBillWave,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const MyOrder = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const userId = localStorage.getItem('userId');
    const userMobile = localStorage.getItem('userMobile');

    if (!userId || !userMobile) {
      navigate('/login');
      return;
    }

    // Fetch orders for the logged-in user
    const userOrdersRef = dbRef(database, `Dr/orders/${userMobile}`);

    const unsubscribe = onValue(
      userOrdersRef,
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            const data = snapshot.val();
            const ordersArray = [];

            // Convert object to array
            Object.keys(data).forEach((orderNumber) => {
              const order = data[orderNumber];
              ordersArray.push({
                ...order,
                orderNumberKey: orderNumber,
              });
            });

            // Sort by creation date (newest first)
            ordersArray.sort((a, b) => b.createdAt - a.createdAt);

            setOrders(ordersArray);
            setLoading(false);
          } else {
            setOrders([]);
            setLoading(false);
          }
        } catch (err) {
          console.error('Error fetching orders:', err);
          setError('Failed to load orders');
          setLoading(false);
        }
      },
      (error) => {
        console.error('Firebase error:', error);
        setError('Failed to connect to database');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [navigate]);

  // Get status color and icon
  const getStatusInfo = (status) => {
    switch (status) {
      case 'Confirmed':
        return {
          color: 'bg-green-100 text-green-800 border-green-300',
          icon: <FaCheckCircle />,
        };
      case 'Pending Verification':
        return {
          color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
          icon: <FaClock />,
        };
      case 'Cancelled':
        return {
          color: 'bg-red-100 text-red-800 border-red-300',
          icon: <FaTimesCircle />,
        };
      case 'Dispatched':
        return {
          color: 'bg-blue-100 text-blue-800 border-blue-300',
          icon: <FaTruck />,
        };
      case 'Delivered':
        return {
          color: 'bg-purple-100 text-purple-800 border-purple-300',
          icon: <FaCheckCircle />,
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800 border-gray-300',
          icon: <FaBox />,
        };
    }
  };

  // Mark order as delivered (customer confirms receipt)
  const handleMarkDelivered = async (order) => {
    const userMobile = localStorage.getItem('userMobile');
    if (!userMobile) {
      alert('User not logged in');
      return;
    }

    try {
      const orderRef = dbRef(
        database,
        `Dr/orders/${userMobile}/${order.orderNumberKey}`
      );
      await update(orderRef, {
        orderStatus: 'Delivered',
        deliveredAt: Date.now(),
        updatedAt: Date.now(),
      });
      alert('Order marked as delivered! Thank you for confirming.');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order status');
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center">
            <FaShoppingBag className="mr-3 text-teal-600" />
            My Orders
          </h1>
          <p className="text-gray-600">Track and manage your orders</p>
        </motion.div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-16">
            <FaSpinner className="text-5xl text-teal-500 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Loading your orders...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <FaExclamationTriangle className="text-5xl text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        ) : orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-lg p-16 text-center"
          >
            <FaBox className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Orders Yet</h3>
            <p className="text-gray-600 mb-6">You haven't placed any orders yet</p>
            <button
              onClick={() => navigate('/product')}
              className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
            >
              Start Shopping
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => {
              const statusInfo = getStatusInfo(order.orderStatus);
              return (
                <motion.div
                  key={order.orderNumberKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden border border-gray-200"
                >
                  <div className="p-6">
                    {/* Order Header */}
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-3 py-1 rounded-lg font-bold text-sm">
                          Order #{order.orderNumberKey}
                        </div>
                        <span className="text-gray-500 text-sm">{order.orderId}</span>
                      </div>
                      <div
                        className={`px-4 py-2 rounded-lg text-sm font-bold border flex items-center gap-2 ${statusInfo.color}`}
                      >
                        {statusInfo.icon}
                        {order.orderStatus}
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src={order.product?.image}
                        alt={order.product?.name}
                        className="w-20 h-20 object-cover rounded-xl"
                      />
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800 text-lg mb-1">
                          {order.product?.name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>Qty: {order.product?.quantity}</span>
                          <span className="font-bold text-teal-600">
                            ₹{order.product?.totalPrice}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Order Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-4">
                      <div className="flex items-center text-gray-700">
                        <FaCalendar className="mr-2 text-teal-500" />
                        <span>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <FaCreditCard className="mr-2 text-teal-500" />
                        <span>{order.paymentDetails?.method}</span>
                      </div>
                      {order.deliveryDetails?.expectedDeliveryDate && (
                        <div className="flex items-center text-gray-700">
                          <FaTruck className="mr-2 text-teal-500" />
                          <span>
                            Expected:{' '}
                            {new Date(
                              order.deliveryDetails.expectedDeliveryDate
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Cancellation Reason */}
                    {order.orderStatus === 'Cancelled' &&
                      order.cancellationReason && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                          <p className="text-sm text-red-800">
                            <strong>Cancellation Reason:</strong>{' '}
                            {order.cancellationReason}
                          </p>
                        </div>
                      )}

                    {/* Delivery Info */}
                    {order.orderStatus === 'Confirmed' &&
                      order.deliveryDetails?.deliveryDays && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
                          <p className="text-sm text-green-800">
                            <FaTruck className="inline mr-2" />
                            <strong>Delivery in:</strong>{' '}
                            {order.deliveryDetails.deliveryDays} days
                          </p>
                        </div>
                      )}

                    {/* Dispatched Info */}
                    {order.orderStatus === 'Dispatched' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                        <p className="text-sm text-blue-800">
                          <FaTruck className="inline mr-2" />
                          <strong>Order Dispatched!</strong> Your order is on the way.
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {/* Mark as Delivered Button - Only for Dispatched Orders */}
                      {order.orderStatus === 'Dispatched' && (
                        <button
                          onClick={() => handleMarkDelivered(order)}
                          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-3 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                        >
                          <FaCheckCircle />
                          Mark as Delivered
                        </button>
                      )}

                      {/* View Details Button */}
                      <button
                        onClick={() => handleViewDetails(order)}
                        className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-3 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                      >
                        <FaBox />
                        View Full Details
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-emerald-500 text-white p-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Order Details</h2>
                    <p className="text-teal-100 text-sm">
                      Order #{selectedOrder.orderNumberKey} -{' '}
                      {selectedOrder.orderId}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                  >
                    <FaTimesCircle className="text-xl" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Product Details */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaShoppingBag className="mr-2 text-purple-600" />
                    Product Information
                  </h3>
                  <div className="flex items-center gap-4">
                    <img
                      src={selectedOrder.product?.image}
                      alt={selectedOrder.product?.name}
                      className="w-24 h-24 object-cover rounded-xl"
                    />
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 text-lg mb-2">
                        {selectedOrder.product?.name}
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Category</p>
                          <p className="font-medium text-gray-800">
                            {selectedOrder.product?.category}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Quantity</p>
                          <p className="font-medium text-gray-800">
                            {selectedOrder.product?.quantity}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Price per Unit</p>
                          <p className="font-medium text-gray-800">
                            ₹{selectedOrder.product?.price}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Total Price</p>
                          <p className="font-bold text-purple-600 text-lg">
                            ₹{selectedOrder.product?.totalPrice}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Details */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-blue-600" />
                    Delivery Information
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Delivery Address</p>
                      <p className="font-medium text-gray-800">
                        {selectedOrder.deliveryDetails?.address}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-600 mb-1">Contact Phone</p>
                        <p className="font-bold text-gray-800">
                          {selectedOrder.deliveryDetails?.phoneNumber}
                        </p>
                      </div>
                      {selectedOrder.deliveryDetails?.expectedDeliveryDate && (
                        <div>
                          <p className="text-gray-600 mb-1">Expected Delivery</p>
                          <p className="font-bold text-gray-800">
                            {new Date(
                              selectedOrder.deliveryDetails.expectedDeliveryDate
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                    {selectedOrder.deliveryDetails?.deliveryDays && (
                      <div className="bg-white p-3 rounded-xl">
                        <p className="text-blue-700 font-medium">
                          <FaTruck className="inline mr-2" />
                          Delivery in {selectedOrder.deliveryDetails.deliveryDays}{' '}
                          days
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Details */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaCreditCard className="mr-2 text-green-600" />
                    Payment Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Payment Method</p>
                      <p className="font-bold text-gray-800">
                        {selectedOrder.paymentDetails?.method}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Payment Status</p>
                      <p
                        className={`font-bold ${
                          selectedOrder.paymentStatus === 'Verified'
                            ? 'text-green-600'
                            : 'text-yellow-600'
                        }`}
                      >
                        {selectedOrder.paymentStatus}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">UPI ID</p>
                      <p className="font-medium text-gray-800">
                        {selectedOrder.paymentDetails?.upiId}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Order Status */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaClock className="mr-2 text-gray-600" />
                    Order Status
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Current Status</span>
                      <span
                        className={`px-3 py-1 rounded-lg font-bold border ${
                          getStatusInfo(selectedOrder.orderStatus).color
                        }`}
                      >
                        {selectedOrder.orderStatus}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Order Date</span>
                      <span className="font-bold text-gray-800">
                        {new Date(selectedOrder.createdAt).toLocaleString()}
                      </span>
                    </div>
                    {selectedOrder.confirmedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Confirmed At</span>
                        <span className="font-bold text-gray-800">
                          {new Date(selectedOrder.confirmedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedOrder.deliveredAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Delivered At</span>
                        <span className="font-bold text-gray-800">
                          {new Date(selectedOrder.deliveredAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedOrder.dispatchedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Dispatched At</span>
                        <span className="font-bold text-gray-800">
                          {new Date(selectedOrder.dispatchedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Mark as Delivered Button in Modal */}
                  {selectedOrder.orderStatus === 'Dispatched' && (
                    <div className="mt-4">
                      <button
                        onClick={() => handleMarkDelivered(selectedOrder)}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-3 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                      >
                        <FaCheckCircle />
                        Confirm Order Delivered
                      </button>
                      <p className="text-xs text-gray-500 text-center mt-2">
                        Click this button once you receive your order
                      </p>
                    </div>
                  )}

                  {/* Cancellation Info */}
                  {selectedOrder.orderStatus === 'Cancelled' &&
                    selectedOrder.cancellationReason && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                        <p className="text-sm text-red-800">
                          <strong>Cancellation Reason:</strong>
                          <br />
                          {selectedOrder.cancellationReason}
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyOrder;
